import { $dfs } from '@lexical/utils';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $nodesOfType,
  LexicalEditor,
  LexicalNode,
} from 'lexical';

import {
  $createCasDataTableNode,
  CasTableDataNode,
  IDataTableConfig,
} from 'src/compass/shared/components/CasLexicalRichTextEditor/CasPlugins/CasTableDataPlugin/CasTableDataNode';
import {
  $createImageNode,
  $isImageNode,
} from 'src/compass/shared/components/CasLexicalRichTextEditor/lexical-playground/src/nodes/ImageNode';
import {
  dataUriSize,
  displaySize,
} from 'src/compass/shared/components/CasLexicalRichTextEditor/lexical-playground/src/utils/clipboardPictures';

type DataObjectType = IDataTableConfig['dataObject'];

export interface SmsBodyType {
  text: string;
  dataObjects: DataObjectType[];
  /*
    The body's pictures, as the data: URIs the editor holds, each once and in the order they
    appear. A text carries them as attachments (MMS), since it has no place to put them in its words.
  */
  images: string[];
}

/*
  A text message carries neither HTML nor a table, so the rich text body is split
  into the two things SMS mode shows separately: the words, as plain text, and the
  data objects, as their own list below the body.

  Both are read from the editor state rather than from the serialized HTML. A data
  object is a DecoratorNode, so it contributes no text of its own - getTextContent
  already returns the body without it, which is the "strip" half - and the node
  carries its data object config directly, so the list needs no re-parsing of the
  exported <dataObject> attribute.
*/
export const splitBodyForSms = (editor: LexicalEditor): SmsBodyType =>
  editor.getEditorState().read(() => {
    const root = $getRoot();
    const tables = $nodesOfType(CasTableDataNode);

    // Walked in document order, unlike $nodesOfType, so the pictures attach as they read.
    const imageNodes = $dfs(root)
      .map(({ node }) => node)
      .filter($isImageNode);

    /*
      A data object sits in a block of its own. Reading the root's text straight
      through would still emit that block's separator, so the object leaves a gap
      behind exactly where the template had none. Drop the whole block instead -
      but only when the object was all it held, so "See table: <object>" keeps its
      words. A block the user genuinely left blank is untouched, since that spacing
      was typed on purpose. A pasted picture is the same: it goes as an attachment,
      so the line it sat on goes with it.
    */
    const emptied = new Set(
      [...tables, ...imageNodes]
        .map((node) => topLevelBlockOf(node, root.getKey()))
        .filter((block) => block !== null && block.getTextContent().trim() === '')
        .map((block) => (block as LexicalNode).getKey())
    );

    const text = root
      .getChildren()
      .filter((block) => !emptied.has(block.getKey()))
      .map((block) => block.getTextContent())
      .join('\n')
      // A safety net for anything else that stacks up - the body should never
      // carry more than one blank line in a row.
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    const images = imageNodes
      .map((node) => node.getSrc())
      .filter((src, i, all) => src.startsWith('data:') && all.indexOf(src) === i);

    return {
      text,
      dataObjects: tables.map((node) => ({ ...node.__dataObject })),
      images,
    };
  });

/*
  What SMS mode holds: the text, its pictures and - on SCE Reports - its data objects. Kept as
  splitBodyForSms derived it when SMS mode opened, so leaving it can tell whether the sender changed
  anything there.
*/
export interface SmsSnapshotType {
  text: string;
  images: string[];
  dataObjects?: DataObjectType[];
}

// Whether the sender changed anything in SMS mode: the words, the pictures, or the data objects.
export const smsChangedSince = (now: SmsSnapshotType, derived: SmsSnapshotType) =>
  now.text.trim() !== derived.text.trim() ||
  now.images.length !== derived.images.length ||
  now.images.some((src, i) => src !== derived.images[i]) ||
  JSON.stringify(now.dataObjects ?? []) !== JSON.stringify(derived.dataObjects ?? []);

// Whether the email Body holds anything a replacement would lose: words, a picture or a table.
export const bodyHasContent = (editor: LexicalEditor) =>
  editor.getEditorState().read(() => {
    const root = $getRoot();

    return (
      root.getTextContent().trim() !== '' ||
      $dfs(root).some(({ node }) => node instanceof CasTableDataNode || $isImageNode(node))
    );
  });

/*
  The email Body replaced by what SMS mode holds: the text, one paragraph per line, then its data
  objects as tables (SCE Reports passes renderDataObjectUrl), then its pictures at the bottom.

  A text has no formatting to give back, so the Body has none afterwards - which is why single send
  asks the sender first whenever the Body had anything to lose (bodyHasContent), and Batch Send only
  does it once the SMS content has actually changed (smsChangedSince).

  Synchronous: the update is discrete, so the editor holds the new Body by the time this returns.
  Batch Send depends on that - it writes a row's SMS content back into the row's editor as the row
  is left, before that same editor is pointed at the next row.
*/
export function replaceBodyWithSms(
  editor: LexicalEditor,
  sms: SmsSnapshotType,
  renderDataObjectUrl?: string
) {
  // Sized from each picture's own header, which every picture here has: a pasted one is normalized
  // to PNG or JPEG, and a saved one was checked for PNG/JPEG/GIF by the server. The Body shows it at
  // the size the email will repeat; one whose header cannot be read takes its natural size.
  const pictures = sms.images
    .filter((src) => src.startsWith('data:'))
    .map((src) => {
      const size = dataUriSize(src);
      return { src, ...(size ? displaySize(size) : {}) };
    });

  editor.update(
    () => {
      const root = $getRoot();
      root.clear();

      sms.text.split('\n').forEach((line) => {
        const block = $createParagraphNode();
        if (line) block.append($createTextNode(line));
        root.append(block);
      });

      if (renderDataObjectUrl) {
        (sms.dataObjects ?? []).forEach((dataObject) =>
          root.append(
            $createParagraphNode().append(
              $createCasDataTableNode({ renderDataObjectUrl, dataObject })
            )
          )
        );
      }

      pictures.forEach((picture) =>
        root.append(
          $createParagraphNode().append(
            $createImageNode({ altText: '', captionsEnabled: false, ...picture })
          )
        )
      );
    },
    { discrete: true }
  );
}

/* The root-level block a node lives in, which is the unit Lexical separates. */
function topLevelBlockOf(node: LexicalNode, rootKey: string): LexicalNode | null {
  let current: LexicalNode | null = node;

  while (current) {
    const parent: LexicalNode | null = current.getParent();

    if (!parent) return null;
    if (parent.getKey() === rootKey) return current;

    current = parent;
  }

  return null;
}
